import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { personnelApi } from '../../services/api';
import PersonnelNameInput from './PersonnelNameInput';

/**
 * Ek Personel Detayları satır listesi.
 * Ad Soyad, Görev/Unvan, Sayı, Başlangıç, Bitiş ve Birim Fiyat ayrı sütunlarda.
 * Ad Soyad alanı personel listesinden otomatik tamamlanır.
 * NewOrganizationPage ve EditOrganizationPage aynı bileşeni kullanır.
 */
const StaffDetailsRows = ({ rows = [], onChange, onRemove }) => {
  // Aktif personel listesi bir kez çekilip tüm satırlarda paylaşılır
  const { data: personnelData } = useQuery({
    queryKey: ['personnel-lookup'],
    queryFn: () => personnelApi.lookup().then(res => res.data),
    staleTime: 5 * 60 * 1000
  });
  const personnel = personnelData?.data || [];

  // Listeden seçim yapılınca boş olan görev alanı personelin unvanıyla doldurulur
  const handleSelectPersonnel = (index, person) => {
    if (person?.unvan && !String(rows[index]?.role || '').trim()) {
      onChange(index, 'role', person.unvan);
    }
  };

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Henüz personel eklenmedi. "Satır Ekle" butonuna tıklayın.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {/* Geniş ekranlarda tek satırlık başlık — her satırda etiket tekrarını önler */}
      <div className="hidden lg:grid grid-cols-12 gap-2 px-3 text-xs font-medium text-muted-foreground">
        <div className="col-span-3">Ad Soyad</div>
        <div className="col-span-2">Görev / Unvan</div>
        <div className="col-span-1">Sayı</div>
        <div className="col-span-2">Başlangıç</div>
        <div className="col-span-2">Bitiş</div>
        <div className="col-span-2">Birim Fiyat (₺)</div>
      </div>

      {rows.map((staff, index) => (
        <div
          key={index}
          className="grid grid-cols-12 gap-2 items-end border border-gray-200 rounded-md p-3 bg-gray-50/50"
        >
          <div className="col-span-12 lg:col-span-3 space-y-1">
            <Label className="text-xs lg:hidden">Ad Soyad</Label>
            <PersonnelNameInput
              personnel={personnel}
              value={staff.staff_name || ''}
              onChange={(name) => onChange(index, 'staff_name', name)}
              onSelect={(person) => handleSelectPersonnel(index, person)}
              placeholder="Personel ara veya yaz"
            />
          </div>
          <div className="col-span-6 lg:col-span-2 space-y-1">
            <Label className="text-xs lg:hidden">Görev / Unvan</Label>
            <Input
              value={staff.role || ''}
              onChange={(e) => onChange(index, 'role', e.target.value)}
              placeholder="Ör: Sedye"
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-6 lg:col-span-1 space-y-1">
            <Label className="text-xs lg:hidden">Sayı</Label>
            <Input
              type="number"
              min="1"
              value={staff.count ?? 1}
              onChange={(e) => onChange(index, 'count', parseInt(e.target.value) || 1)}
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-6 lg:col-span-2 space-y-1">
            <Label className="text-xs lg:hidden">Başlangıç</Label>
            <Input
              type="time"
              value={staff.start_time || ''}
              onChange={(e) => onChange(index, 'start_time', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="col-span-6 lg:col-span-2 space-y-1">
            <Label className="text-xs lg:hidden">Bitiş</Label>
            <Input
              type="time"
              value={staff.end_time || ''}
              onChange={(e) => onChange(index, 'end_time', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          {/* Birim fiyat ve silme aynı hücreyi paylaşır (12 sütunluk grid sınırı) */}
          <div className="col-span-12 lg:col-span-2 space-y-1">
            <Label className="text-xs lg:hidden">Birim Fiyat (₺)</Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={staff.unit_price ?? ''}
                onChange={(e) => onChange(index, 'unit_price', e.target.value)}
                placeholder="0,00"
                className="h-8 text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemove(index)}
                className="text-destructive hover:text-destructive h-8 px-2 shrink-0"
                title="Satırı sil"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StaffDetailsRows;
